import { useAuth, useClerk } from '@clerk/react'
import { useMutation } from '@tanstack/react-query'
import { ArrowRight, Check, LoaderCircle, LockKeyhole, ShieldCheck, ShoppingCart, Sparkles, Zap } from 'lucide-react'
import { useMemo, useState, type PointerEvent } from 'react'
import { ApiError, apiRequest, formatMoney, planLabels, planShortLabels, planPrice } from '../lib/api'
import { useCart } from '../lib/cart'
import type { Plan, Product } from '../types'
import { Modal } from './Modal'
import { ProductImage } from './ProductImage'
import { StatusBadge } from './StatusBadge'

interface CheckoutResponse {
  data: { checkoutUrl: string; reused: boolean }
}

export function ProductShowcase({ product, index = 0 }: { product: Product; index?: number }) {
  const plans = product.plans.length > 0 ? product.plans : (['monthly'] as Plan[])
  const [plan, setPlan] = useState<Plan>(() => (plans.includes('monthly') ? 'monthly' : plans[0]))
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const { isLoaded, isSignedIn, getToken } = useAuth()
  const { openSignIn } = useClerk()
  const { addItem } = useCart()
  const price = planPrice(product, plan)
  // Availability is per license duration — real key counts, not a global counter.
  const planStock = product.stockByPlan?.[plan] ?? product.stock
  const stockState = planStock < 1 ? 'out' : planStock <= 10 ? 'low' : 'ok'
  const monthlySavings = useMemo(() => {
    if (!plans.includes('weekly') || !plans.includes('monthly')) return 0
    const monthlyAtWeeklyRate = product.pricing.weekly * (52 / 12)
    return Math.max(0, Math.round((1 - product.pricing.monthly / monthlyAtWeeklyRate) * 100))
  }, [plans, product.pricing.monthly, product.pricing.weekly])

  const checkout = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      if (!token) throw new ApiError('Sign in to continue.', 'UNAUTHENTICATED', 401)
      return apiRequest<CheckoutResponse>('/api/checkout/session', {
        method: 'POST',
        token,
        json: { productId: product.id, plan },
      })
    },
    onSuccess: ({ data }) => window.location.assign(data.checkoutUrl),
  })

  const startCheckout = () => {
    if (!isLoaded) return
    if (!isSignedIn) {
      void openSignIn({})
      return
    }
    checkout.reset()
    setCheckoutOpen(true)
  }

  const onTilt = (event: PointerEvent<HTMLDivElement>) => {
    const target = event.currentTarget
    const bounds = target.getBoundingClientRect()
    const x = (event.clientX - bounds.left) / bounds.width
    const y = (event.clientY - bounds.top) / bounds.height
    target.style.setProperty('--tilt-x', `${(0.5 - y) * 9}deg`)
    target.style.setProperty('--tilt-y', `${(x - 0.5) * 11}deg`)
    target.style.setProperty('--glare-x', `${x * 100}%`)
    target.style.setProperty('--glare-y', `${y * 100}%`)
    target.dataset.tilting = 'true'
  }

  const resetTilt = (event: PointerEvent<HTMLDivElement>) => {
    const target = event.currentTarget
    target.style.setProperty('--tilt-x', '0deg')
    target.style.setProperty('--tilt-y', '0deg')
    target.dataset.tilting = 'false'
  }

  return (
    <>
      <div className="product-layout">
        <div className="product-story t-stagger" data-visible="true">
          <span className="eyebrow"><span>{String(index + 1).padStart(2, '0')}</span> {product.tag ? product.tag.toUpperCase() : 'ACTIVE PRODUCT'}</span>
          <h2>{product.name}.<br /><em>{product.tagline}</em></h2>
          {product.weao ? (
            <div className="product-status">
              <StatusBadge status={product.weao} />
              {product.weao.version ? <span className="product-status__ver">{/^v/i.test(product.weao.version) ? product.weao.version : `v${product.weao.version}`}</span> : null}
              {product.weao.updatedDate ? <span className="product-status__date">Updated {product.weao.updatedDate}</span> : null}
              <span className="product-status__src">via WEAO</span>
            </div>
          ) : null}
          <p>{product.description}</p>

          <div className="feature-list">
            {product.features.map((feature) => (
              <div className="feature-list__item" key={feature}>
                <span><Check size={14} /></span>
                {feature}
              </div>
            ))}
          </div>

          <div className="trust-inline">
            <span><ShieldCheck size={16} /> Encrypted payment</span>
            <span><Zap size={16} /> Instant key delivery</span>
          </div>
        </div>

        <div
          className="t-tilt product-card-wrap"
          onPointerMove={onTilt}
          onPointerLeave={resetTilt}
          onPointerCancel={resetTilt}
        >
          <article className="product-card t-tilt__card">
            <div className="t-tilt__glare" />
            <div className="product-card__topline">
              <span className="live-indicator"><i /> {planStock > 0 ? 'AVAILABLE' : 'SOLD OUT'}</span>
              <span className="product-card__serial">{product.tag ? product.tag.toUpperCase() : `CE—${String(index + 1).padStart(3, '0')}`}</span>
            </div>

            <div className="volt-visual" style={{ ['--accent' as string]: product.accent }}>
              <div className="volt-visual__orbit" />
              <div className="volt-visual__core">
                <ProductImage name={product.name} alt={`${product.name} symbol`} />
              </div>
              <span className="volt-visual__spark volt-visual__spark--one" />
              <span className="volt-visual__spark volt-visual__spark--two" />
            </div>

            <div className="product-card__identity">
              <div>
                <span className="micro-label">CHEATSECLIPSE ORIGINAL</span>
                <h3>{product.name}</h3>
                <p>{product.tagline}</p>
              </div>
              <Sparkles size={21} />
            </div>

            {plans.length > 1 ? (
              <div className="plan-switch" role="radiogroup" aria-label="License plan" data-plan={plan} data-count={plans.length}>
                <span className="plan-switch__pill" aria-hidden="true" style={{ ['--pill-index' as string]: plans.indexOf(plan) }} />
                {plans.map((option) => (
                  <button key={option} type="button" role="radio" aria-checked={plan === option} onClick={() => setPlan(option)}>
                    {planShortLabels[option]}
                    {option === 'monthly' && monthlySavings > 0 ? <small>−{monthlySavings}%</small> : null}
                  </button>
                ))}
              </div>
            ) : (
              <div className="plan-single">{planLabels[plan]}</div>
            )}

            <div className="price-row" aria-live="polite">
              <div>
                <strong>{formatMoney(price, product.pricing.currency)}</strong>
                <span>{plan === 'lifetime' ? 'one-time · forever' : `for ${planShortLabels[plan]}`}</span>
              </div>
              <span className="stock-chip" data-stock={stockState}>
                {stockState === 'out' ? 'Out of stock' : stockState === 'low' ? `Only ${planStock} left` : `${planStock} in stock`}
              </span>
            </div>

            <div className="buy-row">
              <button className="button button--primary button--checkout" type="button" onClick={startCheckout} disabled={stockState === 'out'}>
                <span>{stockState === 'out' ? 'Sold out' : `Get ${product.name}`}</span>
                <ArrowRight size={18} />
              </button>
              <button
                className="button button--secondary button--cart"
                type="button"
                disabled={stockState === 'out'}
                onClick={() =>
                  addItem({
                    productId: product.id,
                    slug: product.slug,
                    name: product.name,
                    plan,
                    priceCents: price,
                    currency: product.pricing.currency,
                    quantity: 1,
                  })
                }
                aria-label={`Add ${product.name} (${planLabels[plan]}) to cart`}
              >
                <ShoppingCart size={17} />
              </button>
            </div>

            <p className="secure-note"><LockKeyhole size={12} /> Secure checkout handled by Stripe</p>
          </article>
        </div>
      </div>

      <Modal
        open={checkoutOpen}
        onClose={() => !checkout.isPending && setCheckoutOpen(false)}
        title="Confirm your access"
        eyebrow={`CHECKOUT // ${product.name.toUpperCase()}`}
        size="compact"
      >
        <div className="checkout-summary">
          <div className="checkout-summary__product">
            <span><ProductImage name={product.name} /></span>
            <div><strong>{product.name}</strong><small>{planLabels[plan]}</small></div>
          </div>
          <div className="checkout-summary__line"><span>License</span><strong>{planLabels[plan]}</strong></div>
          <div className="checkout-summary__line"><span>Billing</span><strong>One-time payment</strong></div>
          <div className="checkout-summary__total">
            <span>Total today</span>
            <div><strong>{formatMoney(price, product.pricing.currency)}</strong><small>{product.pricing.currency}</small></div>
          </div>
        </div>

        {checkout.error ? (
          <div className="form-message form-message--error" role="alert">
            {checkout.error instanceof Error ? checkout.error.message : 'The payment could not be started.'}
          </div>
        ) : null}

        <button
          className="button button--primary button--wide"
          type="button"
          onClick={() => checkout.mutate()}
          disabled={checkout.isPending}
        >
          {checkout.isPending ? <><LoaderCircle className="spin" size={17} /> Preparing Stripe…</> : <>Continue to Stripe <ArrowRight size={17} /></>}
        </button>
        <p className="modal-legal">One-time purchase — nothing renews automatically. Your license key arrives by email and in your account right after payment.</p>
      </Modal>
    </>
  )
}
